import { Form } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  EmptyItem,
  GroupItem,
  Label,  
  SimpleItem,
} from 'devextreme-react/form';
import DataSource from 'devextreme/data/data_source';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSWR from 'swr';
import { postApi } from '../../apiUtils';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useAuth } from '../../contexts/auth';
import { useTranslation } from '../../contexts/translation';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { Asset } from '../../types/asset.types';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { Customer } from '../../types/customer.types';
import { CustomerProject } from '../../types/customerProject.types';
import { Person } from '../../types/person.types';
import { getUserColorClass, UserColor } from '../../types/userColor.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { dxSelectBoxConfig } from './TasksForm';
import './TasksServiceDesk.scss';

interface CreateFormData {
  id: number;
  userId: number;
  assetClassId: number;
  inboxEmail: string;
  inboxFolderName: string;

  requestorName: string;
  requestorEmail: string;
  requestorPhone?: string;

  customerId: number | null;
  customerProjectId: number | null;
  typeId: number | null;
  topLevelAssetId: number | null;
  assetId: number | null;
  capacityCategoryId: number | null;
  responsiblePersonId: number | null;
}

interface Props {
  defaultValues: CreateFormData;
}

export const TasksTemplateUpdateForm = ({ defaultValues }: Props) => {
  const { assetClass } = useAssetClass();
  const { user } = useAuth();
  const history = useHistory();
  const { translate } = useTranslation();
  const transFormName = 'Task.CreateServiceDesk';

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    defaultValues.customerId || null,
  );
  const [selectedTopLevelAssetId, setSelectedTopLevelAssetId] = useState<number | null>(
    defaultValues.topLevelAssetId || null,
  );
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(
    defaultValues.typeId || null,
  );
  const [userColorClass, setUserColorClass] = useState<string | null>(null);

  const formRef = useRef<any>();
  const formData = useRef<any>(defaultValues);

  const backRoute = '/tasks';
  const [
    prompt,
    setDirty,
    setClean,
    handleBackButton,
    handleHistoryGoBackButton,
    handleDirtyWithCallback,
    isDirty,
  ] = useUnsavedChangesWarning(backRoute, () => {
    if (!formRef) return;
    if (formRef.current!.instance.validate().isValid)
      onSubmit(null).then((result) => {
        if (result) history.push(`/task-templates`);
        else setDirty();
      });
  });

  const { data: customers } = useSWR<Customer[]>(`${API_URL.CUSTOMER_LIST}`, fetcher, swrConfig);
  const { data: projects } = useSWR<CustomerProject[]>(
    selectedCustomerId ? `${API_URL.CUSTOMER_PROJECT_LIST}?parentId=${selectedCustomerId}` : null,
    fetcher,
    swrConfig,
  );
  const { data: documentTypes } = useSWR<any[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClass}&taskCreateDesktop=true&visibleOnly=true&validateForUser=true`,
    fetcher,
    swrConfig,
  );
  const { data: capacityCategories } = useSWR<CapacityCategory[]>(
    `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: topLevelAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}?classId=${assetClass}&activeOnly=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );
  const { data: assets } = useSWR<Asset[]>(
    selectedTopLevelAssetId
      ? `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}&parentId=${selectedTopLevelAssetId}&assetToInclude=${selectedTopLevelAssetId}`
      : `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}`,
    fetcher,
    swrConfig,
  );
  const { data: responsiblePersons } = useSWR<Person[]>(API_URL.PERSON_FROM_THIS_APP_LIST, fetcher);
  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );

  // Načetly se customers, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!customers || customers.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('customerId', customers[0].id);
  }, [formRef, customers]);

  // Načetly se projekty (po výběru customera), automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!projects || projects.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('customerProjectId', projects[0].id);
  }, [formRef, projects]);

  // Načetly se types, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!documentTypes || documentTypes.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('typeId', documentTypes[0].id);
  }, [formRef, documentTypes]);

  // Načetly se categories, automaicky vybereme, pokud je pouze jeden
  useEffect(() => {
    if (!capacityCategories || capacityCategories.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('capacityCategoryId', capacityCategories[0].id);
  }, [formRef, capacityCategories]);

  // Načetly se assety, pokud je pouze jeden, vybereme
  useEffect(() => {
    if (!assets || assets.length !== 1) return;
    if (!formRef || !formRef.current) return;
    formRef.current.instance.updateData('assetId', assets[0].id);
  }, [formRef, assets]);

  // Při změně assetu se vybere parent
  const handleAssetChanged = useCallback(
    (assetId: number) => {
      if (selectedTopLevelAssetId || !assets) return;
      const asset: Asset = assets.filter((x) => x.id === assetId)[0];
      if (!asset) return;
      formRef.current.instance.updateData('topLevelAssetId', asset.parentAssetId);
    },
    [selectedTopLevelAssetId, assets],
  );

  const onSubmit = async (e: any) => {
    if (e !== null) e.preventDefault();
    if (!formData) return;

    let isSuccess = false;

    await postApi({
      url: API_URL.TASK_TEMPLATE_CREATE_UPDATE,
      data: formData.current,
      callAfterSuccess: async (data) => {
        isSuccess = true;
      },
    });

    if (!isSuccess) setDirty();
    else setClean();

    if (isSuccess && e !== null) history.push(`/task-templates`);

    return isSuccess;
  };

  const handleResponsiblePersonChanged = (userColors: UserColor[], userId: number) => {
    const colorClass = getUserColorClass(userColors, userId);
    setUserColorClass(colorClass);
  };

  const projectsWithGroupName = projects?.map((project) => ({
    ...project,
    groupName: project.projectStatusIsActive
      ? translate!('Active projects', transFormName)
      : translate!('Inactive projects', transFormName),
  }));

  if (!userColors || !documentTypes || !assets) return <Loader />;

  return (
    <>
      <form onSubmit={onSubmit}>
        <Form
          formData={formData.current}
          ref={formRef}
          colCount={3}
          onFieldDataChanged={(e) => {
            setDirty();
          }}
        >
          <GroupItem colSpan={3} colCount={3}>
            <SimpleItem
              dataField="inboxEmail"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={translate!('Inbox email', transFormName)} />
            </SimpleItem>
            <SimpleItem
              dataField="inboxFolderName"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={translate!('Inbox folder name', transFormName)} />
            </SimpleItem>
            <SimpleItem
              dataField="requestorEmail"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: true,
              }}
            >
              <Label text={translate!('Requestor email', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <GroupItem colSpan={3}>
            <hr />
          </GroupItem>

          <SimpleItem
            dataField={'customerId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: customers,
              onValueChanged: (e: any) => setSelectedCustomerId(e.value),
            }}
          >
            <Label text={translate!('Customer', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'customerProjectId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: new DataSource({
                store: projectsWithGroupName,
                group: {
                  selector: 'groupName',
                  desc: false,
                },
              }),
              readOnly: !selectedCustomerId,
              grouped: true,
              groupExpr: 'groupName',
            }}
          >
            <Label text={translate!('Project', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'typeId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: documentTypes,
              onValueChanged: (e: any) => setSelectedDocumentTypeId(e.value),
            }}
          >
            <Label text={translate!('Type', transFormName)} />            
          </SimpleItem>
          <SimpleItem
            dataField={'topLevelAssetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: selectedCustomerId
                ? topLevelAssets?.filter(
                    (x) => x.customerId === selectedCustomerId || !x.customerId,
                  )
                : topLevelAssets,
              onValueChanged: (e: any) => setSelectedTopLevelAssetId(e.value),
            }}
          >
            <Label text={translate!('Parent asset', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'assetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: selectedCustomerId
                ? assets?.filter((x) => x.customerId === selectedCustomerId || !x.customerId)
                : assets,
              onValueChanged: (e: any) => handleAssetChanged(e.value),
            }}
          >
            <Label text={translate!('Asset', transFormName)} />            
          </SimpleItem>

          <SimpleItem
            dataField={'capacityCategoryId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: capacityCategories,
            }}
          >
            <Label text={translate!('Capacity category', transFormName)} />            
          </SimpleItem>
          <SimpleItem
            cssClass={`dropdown-user-color ${userColorClass}`}
            dataField="responsiblePersonId"
            editorType="dxSelectBox"
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'personalNumber',
              displayExpr: 'fullName',
              items: responsiblePersons,
              onValueChanged: (e) => handleResponsiblePersonChanged(userColors, e.value),
              itemTemplate: (data: any, _index: number, element: any) => {
                const color = userColors.find((x) => x.userId === data.personalNumber);
                if (color)
                  element.innerHTML = `<div style="color: #${color.color}">${data.fullName}</div>`;
                else element.innerHTML = `<div>${data.fullName}</div>`;
              },
            }}
          >
            <Label text={translate!('Responsible person', transFormName)} />
          </SimpleItem>

          <EmptyItem />
          <EmptyItem />

          <GroupItem cssClass={'requestorGroup'}>
            <SimpleItem dataField="requestorName" editorType="dxTextBox">
              <Label text={translate!('Requestor name', transFormName)} />
            </SimpleItem>
            <SimpleItem dataField="requestorPhone" editorType="dxTextBox">
              <Label text={translate!('Requestor phone', transFormName)} />
              <CustomRule
                message={translate!('Enter valid phone number', transFormName)}
                validationCallback={(e) => {
                  return (
                    !e.value ||
                    /^(\+?[0-9]{1,4}( ?))?[0-9]{3}( ?)[0-9]{3}( ?)[0-9]{3}$/.test(e.value)
                  );
                }}
              />
            </SimpleItem>
          </GroupItem>
          <EmptyItem />
          <EmptyItem />

          <GroupItem colCount={3}>
            <ButtonItem verticalAlignment={'center'} horizontalAlignment={'left'}>
              <ButtonOptions
                width={'100%'}
                text={translate!('Close', transFormName)}
                type={'normal'}
                useSubmitBehavior={false}
                onClick={() =>
                  history.push({
                    pathname: '/task-templates',
                    state: { loadSettingFromSession: true },
                  })
                }
              />
            </ButtonItem>
            <ButtonItem verticalAlignment={'center'} horizontalAlignment={'left'}>
              <ButtonOptions
                width={'100%'}
                text={translate!('Save', transFormName)}
                type={'success'}
                useSubmitBehavior
              />
            </ButtonItem>
          </GroupItem>
        </Form>
      </form>
      {prompt}
    </>
  );
};
